/***
 *
 *   SCORING RUNNER (live preview)
 *   Attaches a client-side score preview to a SurveyJS model for
 *   scoring-enabled forms (risk_assessment / edd). Recomputes the
 *   running score as answers change and feeds the locked
 *   "risk_rating" expression question via survey variables.
 *
 *   Canonical config lives at the schema root:
 *   scoring: { enabled, threshold_medium, threshold_high }
 *
 *   This is a preview aid only — the server result is authoritative.
 *
 **********/

const SCOREABLE_TYPES = new Set([
  "radiogroup",
  "dropdown",
  "checkbox",
  "boolean",
]);

const DEFAULTS = { threshold_high: 70, threshold_medium: 30 };

function normalizeConfig(config) {
  if (!config || typeof config !== "object" || config.enabled !== true) {
    return null;
  }

  const high = Number(config.threshold_high);
  const medium = Number(config.threshold_medium);

  return {
    enabled: true,
    threshold_high: Number.isFinite(high) ? high : DEFAULTS.threshold_high,
    threshold_medium: Number.isFinite(medium)
      ? medium
      : DEFAULTS.threshold_medium,
  };
}

function readScoringConfig(survey, schema) {
  // preferred: the raw stored schema (the canonical `scoring` root key
  // is not a registered SurveyJS property, so the model may not carry it)
  const fromSchema = normalizeConfig(schema?.scoring);
  if (fromSchema) return fromSchema;

  if (survey && typeof survey.getPropertyValue === "function") {
    const fromProperty = normalizeConfig(survey.getPropertyValue("scoring"));
    if (fromProperty) return fromProperty;
  }

  if (survey && typeof survey.toJSON === "function") {
    const fromJson = normalizeConfig(survey.toJSON()?.scoring);
    if (fromJson) return fromJson;
  }

  return null;
}

function recomputeScore(survey) {
  let total = 0;
  survey.getAllQuestions().forEach((q) => {
    const type = q.getType ? q.getType() : null;
    if (!type || !SCOREABLE_TYPES.has(type)) return;

    const value = q.value;
    if (value === undefined || value === null) return;

    if (type === "boolean") {
      const isTrue = value === true || value === "true";
      const s = Number(isTrue ? q.scoreTrue : q.scoreFalse);
      if (Number.isFinite(s)) total += s;
      return;
    }

    const choices = Array.isArray(q.choices) ? q.choices : [];
    if (!choices.length) return;

    const values = Array.isArray(value) ? value : [value];
    values.forEach((v) => {
      const choice = choices.find((c) => c.value === v);
      const score = Number(choice?.score);
      if (Number.isFinite(score)) total += score;
    });
  });
  survey.setVariable("_risk_score", total);
}

export function attachScoring(survey, schema) {
  if (!survey) return () => {};
  const cfg = readScoringConfig(survey, schema);
  if (!cfg) return () => {};

  survey.setVariable("_risk_threshold_high", cfg.threshold_high);
  survey.setVariable("_risk_threshold_medium", cfg.threshold_medium);

  const handler = (sender) => recomputeScore(sender);
  survey.onValueChanged.add(handler);
  recomputeScore(survey);

  return () => survey.onValueChanged.remove(handler);
}
